import type { JSX, RefObject } from 'react'
import type { IFallbackImageProps } from 'atoms/FallbackImage'

import { useRef } from 'react'
import { FallbackImage } from 'atoms/FallbackImage'
import { CardCorners } from 'blocks/Cards/Card.h'
import { PosterState } from 'utils/constants'
import { useLazyLoading, useLoadingImage } from 'utils/hooks'
import { COLOR } from 'vars/index'
import { PosterPlaceholderSC, PosterSC } from './ImagePoster.styled'

export interface IImagePoster {
  posterUrl: string | undefined
  handleLoading?: () => void
  handleError?: () => void
  height?: number
  width?: number
  alt?: string
  fallbackImageSize?: IFallbackImageProps['size']
  fallbackImageIcon?: IFallbackImageProps['icon']
  fallbackImageBorders?: IFallbackImageProps['borders']
  fallbackImageBorderColor?: IFallbackImageProps['borderColor']
  fallbackImageBackground?: IFallbackImageProps['background']
  fallbackImageColor?: IFallbackImageProps['color']
  withBackground?: boolean
  rounded?: boolean
  backgroundColor?: COLOR
  imageRef?: RefObject<HTMLImageElement | null>
  corners?: CardCorners
  lazy?: boolean
}

// check if the poster state has particular state flag enabled
const is = (value: number, state: PosterState): boolean =>
  (value & state) === state

export const ImagePoster = ({
  posterUrl,
  handleLoading,
  handleError,
  alt = '',
  width,
  height,
  fallbackImageSize,
  fallbackImageIcon,
  fallbackImageBorders,
  fallbackImageBorderColor,
  fallbackImageColor,
  fallbackImageBackground,
  withBackground,
  rounded,
  backgroundColor = COLOR.SURFACE_OPACITY_LIGHT_10,
  imageRef,
  corners = CardCorners.CORNERS_0,
  lazy = true,
}: IImagePoster): JSX.Element => {
  const posterRef = useRef<HTMLImageElement>(null)
  const actualRef = imageRef || posterRef

  const { state, onLoad, onError } = useLoadingImage({
    handleLoading,
    handleError,
    poster: actualRef,
  })
  useLazyLoading({ ref: lazy ? actualRef : null, posterUrl })

  return !posterUrl || is(state, PosterState.Error) ? (
    <FallbackImage
      color={fallbackImageColor}
      size={fallbackImageSize}
      icon={fallbackImageIcon}
      borders={fallbackImageBorders}
      borderColor={fallbackImageBorderColor}
      background={fallbackImageBackground}
    />
  ) : (
    <>
      <PosterSC
        alt={alt}
        width={width}
        height={height}
        withBackground={withBackground}
        backgroundColor={backgroundColor}
        rounded={rounded}
        ref={actualRef}
        corners={corners}
        show={is(state, PosterState.Idle)}
        onLoad={onLoad}
        onError={onError}
        {...{
          [lazy ? 'data-src' : 'src']: posterUrl,
        }}
      />
      <PosterPlaceholderSC show={is(state, PosterState.Loading)} />
    </>
  )
}
